// The matchmaking contract between the client and the edge Worker, for every backend (docs/NETTEST.md).
// Whatever backend matched them, players end up with the same thing: a MatchAssignment whose roomUrl carries a
// seat claim for the match's room.

import { parseRoomId, type RoomId } from './ids'
import { expectBoolean, expectMatching, expectObject, expectOneOf, expectString } from './wire'

export const BACKEND_KEYS = ['cloudflare', 'playfab'] as const
export type BackendKey = (typeof BACKEND_KEYS)[number]

/** Edge routes (run_worker_first: /api/*). */
export const NET_API = {
  config: '/api/net/config',
  cloudflareMatchmake: '/api/net/cf/matchmake',
  /** Followed by the room id: a probe room. */
  cloudflareRoom: '/api/net/cf/room/',
  /** Followed by the room id: an online war room. */
  cloudflareWar: '/api/net/cf/war/',
  playfabClaim: '/api/net/pf/claim',
  playfabRoom: '/api/net/pf/room',
} as const
export const CLAIM_PARAM = 'claim'
export const QUEUE_PARAM = 'queue'
/** War queues: the nation the player leads (a playable country code). */
export const COUNTRY_PARAM = 'country'
/** War queues: a private match between whoever brings the same invite code (a friend's challenge link). */
export const INVITE_PARAM = 'invite'
/** The network probe's queue (docs/NETTEST.md). */
export const CLOUDFLARE_PROBE_QUEUE = 'probe-duel'
/** Online Country vs Country Duel (docs/ARCHITECTURE.md §15). */
export const CLOUDFLARE_DUEL_QUEUE = 'duel'

/** Invite codes: 8 characters with no look-alikes (no 0/O, 1/I/L), about 39 bits. */
const INVITE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
const INVITE_LENGTH = 8
export const INVITE_CODE_PATTERN = new RegExp(`^[${INVITE_ALPHABET}]{${INVITE_LENGTH}}$`)

export function newInviteCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(INVITE_LENGTH))
  let code = ''
  // 256 is not a multiple of 31, so this is very slightly biased; a code only has to be hard to guess, not uniform.
  for (const byte of bytes) code += INVITE_ALPHABET[byte % INVITE_ALPHABET.length]
  return code
}
/** The header a PlayFab player proves its identity with (their own entity token), as in PlayFab's entity APIs. */
export const ENTITY_TOKEN_HEADER = 'X-EntityToken'

/** Where a search stands. Every backend reports the same phases, in this order; a backend may skip one. */
export type SearchPhase = { readonly kind: 'signing-in' } | { readonly kind: 'queued' } | { readonly kind: 'allocating' }

export interface MatchAssignment {
  /** The match's id in its backend: the Cloudflare room id or the PlayFab match id. */
  readonly matchId: string
  /** The room's wss URL, carrying this player's seat claim. */
  readonly roomUrl: string
}

// ---- Cloudflare matchmaker: one WebSocket per ticket, JSON updates from the shard ----

export const TICKET_FAILURES = ['timeout', 'busy', 'room_unavailable', 'invite_taken', 'bad_request'] as const
export type TicketFailure = (typeof TICKET_FAILURES)[number]

export type TicketUpdate =
  | { readonly kind: 'queued' }
  | { readonly kind: 'matched'; readonly room: RoomId; readonly roomPath: string }
  | { readonly kind: 'failed'; readonly reason: TicketFailure }

const TICKET_UPDATE_KINDS = ['queued', 'matched', 'failed'] as const
const ROOM_PATH_PATTERN = /^\/api\/net\/[a-z]+\/(room|war)[/?][A-Za-z0-9_.=?/-]+$/

export function parseTicketUpdate(value: unknown): TicketUpdate {
  const wire = expectObject(value, 'ticket update')
  const kind = expectOneOf(wire.kind, TICKET_UPDATE_KINDS, 'ticket update kind')
  if (kind === 'queued') return { kind }
  if (kind === 'matched') return { kind, room: parseRoomId(wire.room), roomPath: parseRoomPath(wire.roomPath) }
  return { kind, reason: expectOneOf(wire.reason, TICKET_FAILURES, 'ticket failure') }
}

// ---- PlayFab: the edge turns a PlayFab match into a seat claim for its proxied room server ----

export interface PlayFabClaimRequest {
  readonly matchId: string
}

export interface RoomPathResponse {
  readonly roomPath: string
}

export function parseRoomPathResponse(value: unknown): RoomPathResponse {
  return { roomPath: parseRoomPath(expectObject(value, 'room path response').roomPath) }
}

function parseRoomPath(value: unknown): string {
  return expectMatching(value, ROOM_PATH_PATTERN, 'room path')
}

// ---- What the edge tells the client it can use (GET /api/net/config) ----
// Only deployment facts travel here; fixed probe settings (queues, regions, timeouts) come from the shared code and
// contract.json, which the client and the Worker are deployed from together.

export type PlayFabBackendConfig = { readonly enabled: false } | { readonly enabled: true; readonly titleId: string }

export interface NetConfig {
  readonly cloudflare: { readonly enabled: boolean }
  readonly playfab: PlayFabBackendConfig
}

export function parseNetConfig(value: unknown): NetConfig {
  const wire = expectObject(value, 'net config')
  const playfab = expectObject(wire.playfab, 'playfab config')
  return {
    cloudflare: { enabled: expectBoolean(expectObject(wire.cloudflare, 'cloudflare config').enabled, 'cloudflare enabled') },
    playfab: expectBoolean(playfab.enabled, 'playfab enabled') ? { enabled: true, titleId: expectString(playfab.titleId, 'playfab title id') } : { enabled: false },
  }
}

// ---- Edge errors: JSON bodies of non-2xx replies ----

export const NET_ERROR_CODES = ['not_configured', 'bad_request', 'unauthorized', 'not_a_member', 'no_server', 'not_found', 'upstream_failed'] as const
export type NetErrorCode = (typeof NET_ERROR_CODES)[number]

export interface NetErrorBody {
  readonly error: NetErrorCode
  readonly message: string
}

export function parseNetErrorBody(value: unknown): NetErrorBody {
  const wire = expectObject(value, 'edge error')
  return { error: expectOneOf(wire.error, NET_ERROR_CODES, 'edge error code'), message: expectString(wire.message, 'edge error message') }
}
